import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es.js"

export default class extends Controller {
  static targets = [
    "input",
    "calendar",
    "timeInput",
    "timeSelectWrapper",
    "horariosContainer",
    "timeWarning",

    // Nuevo (unificado)
    "targetHidden",

    // Compatibilidad (viejo: profesional)
    "profesionalHidden"
  ]

  static values = { horariosMap: Object, autoSubmit: Boolean, allowPast: Boolean, excludeTurnoId: Number }

  horariosMapValueChanged() {
    console.log("horariosMapValueChanged keys:", Object.keys(this.horariosMapValue || {}))
    if (!this.picker) return

    this.applyAllowedDays()

    if (this.picker.selectedDates.length > 0) {
      this.generateTimeSlots(this.picker.selectedDates[0], { preselect: this.hasTimeInputTarget ? this.timeInputTarget.value : null })
    }
  }

  connect() {
    this.availableSlots = []
    this.reservedMinutes = new Set()

    const calendarEl = this.hasCalendarTarget ? this.calendarTarget : this.element

    this.initialDateStr = this.hasInputTarget ? (this.inputTarget.value || "") : ""
    this.initialTimeStr = this.hasTimeInputTarget ? (this.timeInputTarget.value || "") : ""

    this.picker = flatpickr(calendarEl, {
      inline: true,
      mode: "single",
      dateFormat: "Y-m-d",
      locale: Spanish,
      weekStartDay: 1,
      minDate: this.allowPastValue ? null : "today",
      onChange: (selectedDates) => {
        const dates = selectedDates.map(d => d.toISOString().slice(0, 10))
        if (this.hasInputTarget) this.inputTarget.value = dates.join(",")
        this.dispatch("selected", { detail: { dates } })

        if (selectedDates.length > 0) {
          this.generateTimeSlots(selectedDates[0])
        } else {
          this.hideTimeSelect()
        }

        try {
          if (this.autoSubmitValue && dates.length > 0) {
            this.updateAdminTable(dates[0])
          }
        } catch (e) {
          console.error("Error auto-updating admin table", e)
        }
      }
    })

    if (this.initialDateStr) {
      const initialDate = this.picker.parseDate(this.initialDateStr, "Y-m-d")
      if (initialDate) this.picker.setDate(initialDate, false)
    }

    if (Object.keys(this.horariosMapValue || {}).length > 0) {
      this.applyAllowedDays()
    }

    // Inicial: si ya hay target elegido, cargamos agenda
    const key = this.getSelectedKey()
    if (key) {
      this.loadTargetData(key, { keepSelection: true })
        .then(() => this.applyInitialState())
        .catch(e => console.error("Error initial target load", e))
    }

    if (this.hasTimeInputTarget) {
      this.timeInputTarget.addEventListener("change", () => this.validateTimeRange())
    }
  }

  // =========================
  // Nuevo: Target unificado
  // =========================

  async targetSelected(event) {
    let key = event?.detail?.value || event?.detail?.id
    if (!key) return

    // Si viene "5498" -> lo tratamos como profesional
    key = this.normalizeKey(key)

    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key

    // compat: si es prof, llenamos profesionalHidden con matricula
    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    }

    await this.loadTargetData(key)
  }

  async targetChanged(event) {
    let key = event?.target?.value
    if (!key) return
    key = this.normalizeKey(key)
    await this.loadTargetData(key)
  }

  // =========================
  // Compatibilidad: Profesional (viejo)
  // =========================

  async profesionalSelected(event) {
    let v = event?.detail?.value || (this.hasProfesionalHiddenTarget ? this.profesionalHiddenTarget.value : null)
    if (!v) return

    // Puede venir "5498" o "prof:5498" o "srv:12"
    const key = this.normalizeKey(v)

    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    }
    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key

    await this.loadTargetData(key)
  }

  async profesionalChanged(event) {
    let v = event?.target?.value
    if (!v) return

    const key = this.normalizeKey(v)

    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    } else if (this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = ""
    }

    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key
    await this.loadTargetData(key)
  }

  // =========================
  // Core
  // =========================

  applyInitialState() {
    if (!this.picker || !this.initialDateStr) return

    const dateObj = this.picker.parseDate(this.initialDateStr, "Y-m-d")
    if (!dateObj) return

    this.picker.setDate(dateObj, false)
    this.generateTimeSlots(dateObj, { preselect: this.initialTimeStr })
  }

  applyAllowedDays() {
    if (!this.picker) return

    // Tus agenda_info devuelven keys "0".."6" (JS getDay)
    const allowed = Object.keys(this.horariosMapValue || {})
      .map(k => Number(k))
      .filter(n => Number.isInteger(n) && n >= 0 && n <= 6)

    this.picker.set("enable", [])

    if (!allowed.length) {
      this.picker.set("enable", [() => false])
      return
    }

    this.picker.set("enable", [(date) => allowed.includes(date.getDay())])
    this.picker.redraw()
  }

  showTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.remove("d-none")
    if (this.hasTimeInputTarget) this.timeInputTarget.required = true
  }

  hideTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.add("d-none")
    if (this.hasTimeInputTarget) {
      this.timeInputTarget.required = false
      this.populateTimeSelect([], null)
    }
    this.availableSlots = []
  }

  async fetchReservedMinutes(key, dateStr) {
    const params = new URLSearchParams({ fecha: dateStr })

    if (this.hasExcludeTurnoIdValue && this.excludeTurnoIdValue > 0) {
      params.set("exclude_turno_id", String(this.excludeTurnoIdValue))
    }

    if (key.startsWith("prof:")) {
      params.set("profesional_id", key.split(":", 2)[1])
    } else if (key.startsWith("srv:")) {
      params.set("servicio_id", key.split(":", 2)[1])
    } else {
      return new Set()
    }

    try {
      const resp = await fetch(`/turnero/turnos/horarios_reservados?${params.toString()}`)
      if (!resp.ok) return new Set()

      const json = await resp.json()
      return new Set(json.reservados || [])
    } catch (e) {
      console.error("Error fetching reserved times", e)
      return new Set()
    }
  }

  slotIsAvailable(slotStart, reserved, preselect) {
    if (slotStart === preselect) return true
    return !reserved.has(slotStart)
  }

  populateTimeSelect(available, preselect) {
    if (!this.hasTimeInputTarget) return

    const select = this.timeInputTarget
    const previous = preselect || select.value

    select.innerHTML = ""

    const blank = document.createElement("option")
    blank.value = ""
    blank.textContent = "Seleccionar hora..."
    select.appendChild(blank)

    available.forEach(hhmm => {
      const opt = document.createElement("option")
      opt.value = hhmm
      opt.textContent = hhmm
      select.appendChild(opt)
    })

    select.value = (previous && available.includes(previous)) ? previous : ""
  }

  async generateTimeSlots(date, options = {}) {
    const dayOfWeek = date.getDay() // 0..6
    const ranges = this.horariosMapValue ? this.horariosMapValue[dayOfWeek] : null

    if (!ranges || ranges.length === 0) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    const key = this.getSelectedKey()
    if (!key) {
      this.hideTimeSelect()
      return
    }

    const dateStr = this.formatLocalDate(date)
    const preselect = options.preselect || (this.hasTimeInputTarget ? this.timeInputTarget.value : null)

    const reserved = await this.fetchReservedMinutes(key, dateStr)
    this.reservedMinutes = reserved

    const available = []

    ranges.forEach(range => {
      const start = this.hhmmToMinutes(range.inicio)
      const end = this.hhmmToMinutes(range.fin)
      if (start == null || end == null) return

      const step = 15
      for (let m = start; (m + step) <= end; m += step) {
        const hourString = this.minutesToHHMM(m)
        if (this.slotIsAvailable(hourString, reserved, preselect)) {
          available.push(hourString)
        }
      }
    })

    this.availableSlots = available

    if (available.length === 0) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    this.showTimeSelect()
    this.populateTimeSelect(available, preselect)

    if (this.hasHorariosContainerTarget) {
      this.horariosContainerTarget.innerHTML = `<p class='text-muted mb-0'>${available.length} horario${available.length === 1 ? "" : "s"} disponible${available.length === 1 ? "" : "s"}</p>`
    }

    this.validateTimeRange()
  }

  async updateAdminTable(dateStr) {
    try {
      if (this.hasInputTarget) this.inputTarget.value = dateStr
      const form = this.element.closest("form")
      if (!form) return
      form.requestSubmit()
    } catch (e) {
      console.error("Error en updateAdminTable", e)
    }
  }

  async loadTargetData(key, opts = {}) {
    const keepSelection = opts.keepSelection

    try {
      if (!keepSelection) {
        if (this.picker) this.picker.clear()
        if (this.hasInputTarget) this.inputTarget.value = ""
        this.hideTimeSelect()
        if (this.hasHorariosContainerTarget) this.horariosContainerTarget.innerHTML = ""
        this.initialDateStr = ""
        this.initialTimeStr = ""
      }

      // PROFESIONAL
      if (key.startsWith("prof:")) {
        const matricula = key.split(":", 2)[1]
        if (this.hasProfesionalHiddenTarget) this.profesionalHiddenTarget.value = matricula
        await this.loadProfesionalData(matricula)
        return
      }

      // SERVICIO
      if (key.startsWith("srv:")) {
        const serviceId = key.split(":", 2)[1]
        if (this.hasProfesionalHiddenTarget) this.profesionalHiddenTarget.value = ""
        await this.loadServicioData(serviceId)
        return
      }

      // fallback
      this.horariosMapValue = {}
    } catch (e) {
      console.error("Error loading target horarios", e)
      this.horariosMapValue = {}
    }
  }

  async loadProfesionalData(matricula) {
    try {
      const resp = await fetch(`/profesionales/${encodeURIComponent(matricula)}/agenda_info`, {
        headers: { "Accept": "application/json" }
      })
      const data = resp.ok ? await resp.json() : { horarios_map: {} }

      const rawMap = data.horarios_map || {}
      const newMap = {}
      Object.keys(rawMap).forEach(k => { newMap[Number(k)] = rawMap[k] })

      this.horariosMapValue = newMap
    } catch (e) {
      console.error("Error loading agenda profesional", e)
      this.horariosMapValue = {}
    }
  }

  async loadServicioData(serviceId) {
    try {
      const resp = await fetch(`/turnero/servicios/${encodeURIComponent(serviceId)}/agenda_info`, {
        headers: { "Accept": "application/json" }
      })
      const data = resp.ok ? await resp.json() : { horarios_map: {} }

      const rawMap = data.horarios_map || {}
      const newMap = {}
      Object.keys(rawMap).forEach(k => { newMap[Number(k)] = rawMap[k] })

      this.horariosMapValue = newMap
    } catch (e) {
      console.error("Error loading agenda servicio", e)
      this.horariosMapValue = {}
    }
  }

  validateTimeRange() {
    if (this.hasTimeWarningTarget) {
      this.timeWarningTarget.innerHTML = ""
      this.timeWarningTarget.classList.add("d-none")
    }

    if (!this.hasTimeInputTarget || !this.timeInputTarget.value) return

    const selectedTime = this.timeInputTarget.value

    if (this.availableSlots.length > 0 && !this.availableSlots.includes(selectedTime)) {
      if (this.hasTimeWarningTarget) {
        this.timeWarningTarget.innerHTML = "⚠️ El horario seleccionado no está disponible."
        this.timeWarningTarget.classList.remove("d-none")
        this.timeWarningTarget.classList.add("text-warning", "mt-2")
      }
      return
    }

    if (!this.picker || !this.picker.selectedDates || this.picker.selectedDates.length === 0) return

    const selectedDate = this.picker.selectedDates[0]
    const dayOfWeek = selectedDate.getDay()

    const ranges = this.horariosMapValue ? this.horariosMapValue[dayOfWeek] : null
    if (!ranges || ranges.length === 0) return

    let isWithinRange = false
    let minTime = null
    let maxTime = null

    ranges.forEach(range => {
      const rangeStart = range.inicio
      const rangeEnd = range.fin

      if (selectedTime >= rangeStart && selectedTime <= rangeEnd) isWithinRange = true
      if (!minTime || rangeStart < minTime) minTime = rangeStart
      if (!maxTime || rangeEnd > maxTime) maxTime = rangeEnd
    })

    if (!isWithinRange && this.hasTimeWarningTarget) {
      const message = `⚠️ El horario debe estar entre ${minTime} y ${maxTime}`
      this.timeWarningTarget.innerHTML = message
      this.timeWarningTarget.classList.remove("d-none")
      this.timeWarningTarget.classList.add("text-warning", "mt-2")
    }
  }

  timeSelected() {
    this.validateTimeRange()
  }

  // =========================
  // Helpers
  // =========================

  normalizeKey(v) {
    const s = String(v || "").trim()
    if (!s) return ""

    if (s.startsWith("prof:") || s.startsWith("srv:")) return s

    // si es numérico => profesional
    if (/^\d+$/.test(s)) return `prof:${s}`

    return s
  }

  getSelectedKey() {
    if (this.hasTargetHiddenTarget && this.targetHiddenTarget.value) {
      return this.normalizeKey(this.targetHiddenTarget.value)
    }

    if (this.hasProfesionalHiddenTarget && this.profesionalHiddenTarget.value) {
      return this.normalizeKey(this.profesionalHiddenTarget.value)
    }

    return null
  }

  hhmmToMinutes(hhmm) {
    const s = (hhmm || "").toString().trim()
    if (!s) return null
    const [h, m] = s.split(":").map(v => parseInt(v, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    return h * 60 + m
  }

  minutesToHHMM(mins) {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  formatLocalDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}